import React, { useEffect, useRef, useState } from 'react'
import { Button, Flex, HStack, Stack, Text } from '@chakra-ui/react'

import { IProduct, ISavedModifier } from '@/app/[locale]/_types'
import { IModifierQuantityObj, IRedux } from '@/app/[locale]/_store/types'
import { useTranslations } from 'next-intl'
import Modifiers from '../../../../../Modifiers'
import { useCustomLocale } from '@/app/[locale]/_hooks/useCustomLocale'
import { useSelector } from 'react-redux'
import NumberToPrice from '@/app/[locale]/_components/NumberToPrice'
import {
  getMissingRequiredModifierNames,
  validateRequiredModifiers,
} from '@/app/[locale]/_utils/modifier/validateRequiredModifiers'

interface IProps {
  data: IProduct | null | undefined
  onAdd: (e: any) => void
}

function Details({ data, onAdd }: IProps) {
  const [loading, setLoading] = useState(true)
  const [modifiersPrice, setModifiersPrice] = useState(0)
  const [modifiersQuantity, setModifiersQuantity] = useState<
    IModifierQuantityObj[]
  >([])
  const [startTracking, setStartTracking] = useState(false)
  const [orderModifiers, setOrderModifiers] = useState<ISavedModifier[]>([])
  const [compulsories, setCompulsories] = useState<ISavedModifier[]>([])
  const [expectedModifier, setExpectedModifier] = useState<string[]>([])

  const t = useTranslations()
  const currentLocale = useCustomLocale()
  const scrollToExpectedModifierRef = useRef<HTMLDivElement>(null)
  const { branch } = useSelector((state: IRedux) => state.common)

  const onOrderClick = () => {
    if (data?.id && checkModifierRequired()) {
      onAdd(orderModifiers)
    } else {
      setStartTracking(true)
    }
  }

  const checkModifierRequired = () =>
    validateRequiredModifiers(compulsories, orderModifiers, modifiersQuantity)

  useEffect(() => {
    if (startTracking && compulsories?.length > 0) {
      setExpectedModifier(
        getMissingRequiredModifierNames(
          compulsories,
          orderModifiers,
          modifiersQuantity,
          currentLocale
        )
      )
    }
  }, [compulsories, orderModifiers, modifiersQuantity, startTracking, currentLocale])

  useEffect(() => {
    if (!expectedModifier?.length) return
    scrollToExpectedModifierRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })
  }, [expectedModifier])

  useEffect(() => {
    if (orderModifiers) {
      let sum = 0
      for (const modifier of orderModifiers) {
        sum += modifier.modifier_price * modifier.modifier_quantity
      }
      setModifiersPrice(sum)
    }
  }, [orderModifiers])

  // useEffect(() => {
  //   if (selectedVariant?.order_modifiers?.length > 0) {
  //     setOrderModifiers(selectedVariant?.order_modifiers)
  //   }
  // }, [selectedVariant])

  if (!data) {
    return null
  }

  const shouldShow =
    (branch?.menu_id && data.active_in_menu) || !branch?.menu_id

  return (
    <Stack borderColor='gray.200' gap={0} justify='space-between' flex={1}>
      <Stack
        // maxH={{
        //   md:
        //     data?.ingredients?.length > 0 && data?.allergens?.[currentLocale]
        //       ? '500px'
        //       : '370px',
        // }}
        overflowY='auto'
        position='relative'
        gap={4}
        pr={{ md: 2 }}
        pb={{ md: 2 }}
        px={{ base: 4, lg: 0 }}
      >
        <Modifiers
          setLoading={setLoading}
          productInCart={null}
          product={data}
          quantity={1}
          expectedModifiers={expectedModifier}
          onSaveCompulsories={(e: any[]) => setCompulsories(e)}
          orderModifiers={orderModifiers}
          modifiersQuantity={modifiersQuantity}
          setOrderModifiers={setOrderModifiers}
          setModifiersQuantity={setModifiersQuantity}
          scrollToExpectedModifierRef={scrollToExpectedModifierRef}
        />
      </Stack>
      <Stack
        position='sticky'
        bottom={0}
        left={0}
        bg='background.card'
        color='text'
        zIndex={10}
        w='full'
        gap={{ base: 4, lg: 6 }}
        pt={4}
        pb={{ base: 4, lg: 2 }}
        px={{ base: 4, lg: 0 }}
        borderTop='1px solid #00000014'
        boxShadow={{ base: '0 -4px 6px -2px #00000014', lg: 'none' }}
      >
        <Flex
          w='full'
          justifyContent='space-between'
          fontSize='sm'
          fontWeight={600}
        >
          <Text fontSize={{ base: 'sm', lg: 'xl' }} fontWeight={500} flex={1}>
            {data?.title?.[currentLocale]}
          </Text>

          <HStack
            fontSize={{ base: 'sm', lg: '2xl' }}
            fontWeight={600}
            color='text'
          >
            <p>+</p>
            <NumberToPrice value={modifiersPrice} unstyled />
          </HStack>
        </Flex>
        <Flex gap={4} w='full'>
          {shouldShow ? (
            <Button
              onClick={() => onOrderClick()}
              flex={1}
              bg='primary'
              color='primary.text'
              _hover={{
                bg: 'primary.hover',
              }}
              rounded={16}
              fontWeight={600}
              disabled={!data}
              fontSize={{ base: 'sm', lg: 'xl' }}
              height={{ base: '52px', lg: '56px' }}
              loading={loading}
            >
              {expectedModifier?.length > 0
                ? `${t('select_smth', {
                    item:
                      expectedModifier[0]?.length > 20
                        ? expectedModifier[0]?.slice(0, 20) + '...'
                        : expectedModifier[0],
                  })}`
                : t('add_to_cart')}
            </Button>
          ) : (
            <Button variant='subtle' disabled flex={1}>
              {t('will_be_later')}
            </Button>
          )}
        </Flex>
      </Stack>
    </Stack>
  )
}

export default Details
